// MIT License - Copyright (c) 2026 Doug McNall
//
// Cookie-based JWT auth context for the admin app.
//
// Tokens live in HttpOnly cookies set by the gateway; this context can't
// read them. Authentication state is derived from /auth/ping calls — if
// the gateway returns 200 we have a valid session, 401 means logged out.
//
// On mount we ping once to detect an existing session (carried over via
// cookies from a previous tab/visit). After login we also call ping to
// hydrate the user object before rendering authenticated UI.
import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();
const apiUrl = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL;

const _csrfToken = () => {
  const m = document.cookie.match(/(?:^|;\s*)ddhs_admin_csrf=([^;]+)/);
  return m ? m[1] : '';
};

// Probe the gateway for a valid session. Returns the user object on
// success, null on 401, throws on network error.
const _ping = async () => {
  const res = await fetch(`${apiUrl}/auth/ping`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`ping failed: ${res.status}`);
  const data = await res.json();
  return {
    email: data.email,
    attributes: { email: data.email, admin: true },
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: detect existing session via cookie. If there's a stale access
  // cookie that's expired, the gateway returns 401 and we sit at the login
  // screen — refresh-on-load is reactive (next 401 triggers refresh in api.js).
  useEffect(() => {
    (async () => {
      try {
        const u = await _ping();
        setUser(u);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        credentials: 'include',  // accept the Set-Cookie response
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.detail || err.message || 'Login failed' };
      }
      // Cookies are now set by the response. Hydrate the user via /ping
      // (rather than parsing the body) so there's one source of truth.
      const u = await _ping();
      setUser(u);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const csrf = _csrfToken();
      await fetch(`${apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf && { 'X-CSRF-Token': csrf }),
        },
      }).catch(() => {});
    } finally {
      setUser(null);
    }
    return { success: true };
  };

  // Stubs preserved for compatibility with AdminLogin.js — legacy
  // signup/confirmation flows aren't supported in the local-JWT model.
  const register = async () => ({ success: false, error: 'Not supported' });
  const confirmRegistration = async () => ({ success: false, error: 'Not supported' });
  const forgotPassword = async () => ({ success: true });
  const confirmForgotPassword = async () => ({ success: true });
  const changePassword = async () => ({ success: true });

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated: !!user,
      login, logout, register, confirmRegistration,
      forgotPassword, confirmForgotPassword, changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
